import { createRoot, Root } from "react-dom/client";
import React, { Suspense, useRef, useEffect, useState } from "react";
import classNames from "classnames";

import Loading from "../../components/Loading/Loading";
import ToolFormWithContext from "../formContext/ToolFormWithContext";
import { SimulatorProvider } from "../formContext/FormContext";
import { useForm } from "../formContext/hooks/useForm";
import { useResult as useResultHook } from "../formContext/hooks/useResult";
import OnLoadScrollDesktop from "../ToolForm/OnLoadScrollDesktop";
import { utils } from "../../helpers";

const ERROR_MESSAGES = {
  ROOT_ELEMENT_NOT_FOUND: "Elemento raiz não encontrado.",
  CTA_DATA_MISSING: "Atributo data-tool ausente.",
  INITIALIZATION_ERROR: "Erro ao inicializar a ferramenta.",
  RENDER_ERROR: "Erro ao renderizar a ferramenta.",
};

type ToolBlockProps = {
  toolKey: string;
  formConfig: any;
  Results: React.FC;
  Footers?: React.FC;
  Listeners?: React.FC[];
  formClassName?: string;
  showResultAfterForm?: string; // can be: 'below' - default, 'after', 'instead'
  BeforeSubmit?: React.FC[];
  ExtraField?: React.FC[];
  /**
   * OnLoadScrollDesktop (settle-then-scroll-to-results) is on by default for
   * every tool that goes through renderToolBlockInstance. Set this to true
   * to opt a tool out entirely. Rendered at the Tool level (a permanent
   * sibling of ToolInner, not inside the form/results conditional), so it
   * stays mounted across showResultAfterForm's form<->results swap —
   * unlike a Listener, which unmounts with the form in "instead" mode.
   */
  disableAutoScroll?: boolean;
};

/**
 * Sets up form data in context
 */
const FormSetup: React.FC<{ toolKey: string; formConfig: any }> = ({
  toolKey,
  formConfig,
}) => {
  const { updateFormData } = useForm();
  const hasInitialized = useRef(false);

  useEffect(() => {
    if (hasInitialized.current) return;
    try {
      updateFormData({ type: toolKey, data: formConfig });
      hasInitialized.current = true;
    } catch (err) {
      console.error(`[${toolKey}] ${ERROR_MESSAGES.INITIALIZATION_ERROR}`, err);
    }
  }, [toolKey, formConfig]);

  return null;
};

/**
 * Core UI for a tool block
 */
const ToolInner: React.FC<{
  Results: React.FC;
  Footers?: React.FC;
  Listeners?: React.FC[];
  formClassName?: string;
  showResultAfterForm?: string;
  BeforeSubmit?: React.FC[];
  ExtraField?: React.FC[];
}> = ({
  Results,
  Footers,
  Listeners,
  formClassName,
  showResultAfterForm = "below",
  BeforeSubmit,
  ExtraField,
}) => {
  const { formData } = useForm();
  const { resultData } = useResultHook();

  const [isMiniTool, setIsMiniTool] = useState(false);

  useEffect(() => {
    setIsMiniTool(utils.isMiniToolBlock());
  }, []);

  const isEmptyState = !resultData.loading && !resultData.response;

  const containerClass = classNames(
    "flex flex-col pb-8 lg:pb-16 lg:grid lg:grid-cols-12",
    !isMiniTool && "border-b-0 md:border-b border-grey-20",
    isEmptyState && !resultData.justResult ? "gap-0 lg:gap-12" : "gap-12",
  );

  const formClass = classNames(
    "tool-block__component__sidebar",
    // default layout
    !formClassName && "col-span-4",
    // override / extend from parent
    formClassName,
  );

  const resultClass = classNames({
    "tool-block__component__results": true,
    "col-span-8": !resultData.justResult,
    "col-span-12": resultData.justResult,
  });

  let formContent: React.ReactNode = null;
  if (showResultAfterForm === "instead") {
    formContent =
      resultData.response == null ? (
        formData && (
          <ToolFormWithContext
            listeners={Listeners}
            beforeSubmit={BeforeSubmit}
            extraField={ExtraField}
          />
        )
      ) : (
        <div className={resultClass}>
          {formData && !resultData.justResult && <Results />}
        </div>
      );
  } else {
    formContent = (
      <>
        {formData && (
          <ToolFormWithContext
            listeners={Listeners}
            beforeSubmit={BeforeSubmit}
            extraField={ExtraField}
          />
        )}

        {showResultAfterForm !== "below" && (
          <div className={resultClass}>
            {formData && !resultData.justResult && <Results />}
          </div>
        )}
      </>
    );
  }

  return (
    <Suspense fallback={<Loading isFull={false} message="" overlay={false} />}>
      <div className="tool-block__component">
        <div className={containerClass}>
          {!resultData.justResult && (
            <div className={formClass}>{formContent}</div>
          )}

          {"below" === showResultAfterForm && (
            <div className={resultClass}>
              {(formData || resultData.justResult) && <Results />}
            </div>
          )}
        </div>
        {Footers && <Footers />}
      </div>
    </Suspense>
  );
};

/**
 * Simulator wrapper with context
 */
const Tool: React.FC<ToolBlockProps> = ({
  toolKey,
  formConfig,
  Results,
  Footers,
  Listeners,
  formClassName,
  showResultAfterForm,
  BeforeSubmit,
  ExtraField,
  disableAutoScroll,
}) => (
  <SimulatorProvider>
    <FormSetup toolKey={toolKey} formConfig={formConfig} />
    {!disableAutoScroll && <OnLoadScrollDesktop />}
    <ToolInner
      Results={Results}
      Footers={Footers}
      Listeners={Listeners}
      formClassName={formClassName}
      showResultAfterForm={showResultAfterForm}
      BeforeSubmit={BeforeSubmit}
      ExtraField={ExtraField}
    />
  </SimulatorProvider>
);

/**
 * Public helper — use this in view.tsx of each tool
 */

type ToolBlockElement = HTMLElement & {
  __root?: Root;
};

export function renderToolBlockInstance({
  toolKey,
  formConfig,
  Results,
  Footers,
  Listeners,
  formClassName,
  showResultAfterForm,
  BeforeSubmit,
  ExtraField,
  disableAutoScroll,
}: ToolBlockProps) {
  const waitForResources = (): Promise<void> => {
    return new Promise<void>((resolve) => {
      if (document.readyState === "complete") {
        resolve();
      } else {
        window.addEventListener("load", () => resolve(), { once: true });
      }
    });
  };

  waitForResources().then(() => {
    document
      .querySelectorAll<ToolBlockElement>('[data-block="tool-block"]')
      .forEach((el) => {
        try {
          // Updated to React 19
          const root = el.__root ?? createRoot(el);
          el.__root = root;

          root.render(
            <Tool
              toolKey={toolKey}
              formConfig={formConfig}
              Results={Results}
              Footers={Footers}
              Listeners={Listeners}
              formClassName={formClassName}
              showResultAfterForm={showResultAfterForm}
              BeforeSubmit={BeforeSubmit}
              ExtraField={ExtraField}
              disableAutoScroll={disableAutoScroll}
            />,
          );
        } catch (error) {
          console.log(`[${toolKey}] ${ERROR_MESSAGES.RENDER_ERROR}`);
          console.error(`[${toolKey}] ${ERROR_MESSAGES.RENDER_ERROR}`, error);
        }
      });
  });
}
